import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { CustomInput, CustomTextarea } from "@components/Forms";
import { Breadcrumb, FileUploader } from "@components/UI";
import FullScreenDotLoader from "@components/UI/FullScreenDotLoader";
import queryClient from "@configs/react-query/query-client";
import queryKeys from "@configs/react-query/query-key";
import { showErrorToast, showSuccessToast } from "@configs/react-toastify/snackbar";
import { useFileGroupAPI } from "@hooks/useFileGroupAPI";
import { useMutationData } from "@hooks/useMutationData";
import { useTheme } from "@hooks/useTheme";
import { uppercase } from "@utils/common";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { updateDataInIndexedDb, STORES } from "@utils/indexedDb";
import { CHARACTER_LENGTH } from "@constants/index";

export default function QAAdd() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { uploadFilesWithCustomCallback } = useFileGroupAPI(0);

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    openFlag: "Y", // Default to No
    private: false,
    question: "",
  });

  // File upload state
  const [selectedFiles, setSelectedFiles] = useState([]);

  // Validation state
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    validateForm();
  }, [formData]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = t("qa.title-required");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const createQA = useMutationData(
    queryKeys.addQA,
    { method: "POST" },
    {
      onSuccess: async (response) => {
        await updateDataInIndexedDb(STORES.QNAS, response, response.qnaSeq);
        showSuccessToast(t("qa.add-qa-successfully"));
        queryClient.invalidateQueries(queryKeys.getQAList);

        // Handle file upload if files are selected
        if (selectedFiles.length > 0) {
          const formDataForUpload = new FormData();
          formDataForUpload.append("targetSeq", response.qnaSeq);
          formDataForUpload.append("targetTypeCd", "QNA");
          formDataForUpload.append("fileGroupNo", response.fileGroupNo ? response.fileGroupNo : 0);

          const uploadFilesData = selectedFiles.reduce((acc, file, index) => {
            if (file.file instanceof File) {
              acc[`file_${index}`] = file.file;
            }
            return acc;
          }, {});

          Object.entries(uploadFilesData).forEach(([key, file]) => {
            formDataForUpload.append(key, file, file.name);
          });

          uploadFilesWithCustomCallback.mutate(formDataForUpload, {
            onSuccess: () => {
              showSuccessToast(t("common.files-upload-success"));
              navigate(`/question-and-answer/${response.qnaSeq}`);
            },
            onError: (error) => {
              console.error("Error during upload data:", error);
              showErrorToast(t("common.files-upload-error"));
              navigate(`/question-and-answer/${response.qnaSeq}`);
            },
          });
        } else {
          navigate(`/question-and-answer/${response.qnaSeq}`);
        }
      },
      onError: (error) => {
        console.error("Error during create Q&A:", error);
        showErrorToast(t("qa.add-qa-failed"));
        setIsSubmitting(false);
      },
    },
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    await createQA.mutateAsync(formData);
  };

  const handleCancel = () => {
    navigate("/question-and-answer");
  };

  const isFormValid = formData.title.trim() && formData.question.trim();

  return (
    <div data-testid="qa-add-page">
      {/* Header */}
      <div className={`${colors.bgSecondary} ${colors.borderPrimary}`} data-testid="qa-add-header">
        <div className="flex items-center space-x-4">
          <Breadcrumb
            data-testid="qa-add-breadcrumb"
            items={[
              { label: t("bulletin.qna-list-title"), href: "/question-and-answer" },
              { label: t("bulletin.add-qna-button") },
            ]}
          />
        </div>

        <div className="flex items-center justify-between mt-4">
          <h1 className={`${colors.textPrimary} text-2xl font-bold`} data-testid="qa-add-page-title">
            {uppercase(t("bulletin.add-qna-button"))}
          </h1>

          <div className="flex items-center space-x-3" data-testid="qa-add-header-actions">
            <OutlineButton
              data-testid="qa-add-cancel-button"
              onClick={handleCancel}
              className={`rounded-lg border px-4 py-1 font-semibold focus:outline-none ${colors.textSecondary} ${colors.borderHoverPrimary} ${colors.textHoverActive}`}
            >
              {t("common.cancel-button")}
            </OutlineButton>

            <PrimaryButton data-testid="qa-add-save-button" onClick={handleSubmit} disabled={!isFormValid || createQA.isPending}>
              {createQA.isPending ? (
                <div className="flex items-center">
                  <div className={`mr-2 h-4 w-4 animate-spin rounded-full border-b-2 ${colors.borderOnPrimary}`}></div>
                  {t("common.saving")}
                </div>
              ) : (
                t("common.save-button")
              )}
            </PrimaryButton>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`mt-5 p-6 ${colors.bgPrimary} rounded-2xl`} data-testid="qa-add-form">
        <div className={`mx-auto max-w-[56rem]`}>
          <div>
            {/* Q&A Information Section */}
            <div className="mb-6" data-testid="qa-add-information-section">
              <h2
                className={`${colors.textPrimary} text-xl font-semibold pt-2 pb-6`}
                data-testid="qa-add-information-title"
              >
                {t("qa.qa-information")}
              </h2>

              <div className="space-y-6">
                {/* Title and Disclose Row */}
                <div className="flex gap-6 items-end">
                  {/* Title - takes up most of the space */}
                  <div className="flex-1">
                    <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                      {t("qa.title")} <span className={colors.textRequired}>*</span>
                    </label>
                    <CustomInput
                      data-testid="qa-add-title-input"
                      type="text"
                      value={formData.title}
                      onChange={(e) => handleInputChange("title", e.target.value)}
                      placeholder={t("qa.title-placeholder")}
                      error={errors.title}
                      maxLength={CHARACTER_LENGTH.TITLE}
                      className={`h-10 w-full rounded-lg border px-3 py-2 text-sm font-medium focus:outline-none truncate ${colors.borderPrimary} ${colors.bgInput} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary} transition-colors duration-300`}
                    />
                  </div>

                  {/* Disclose Toggle - compact on the right */}
                  {/* <div className="w-32">
                    <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                      {t("qa.disclose")}
                    </label>
                    <div className="flex items-center pt-2 mb-2">
                      <label className="relative inline-flex cursor-pointer hover:cursor-pointer items-center justify-start">
                        <input
                          data-testid="qa-add-disclose-checkbox"
                          type="checkbox"
                          className="sr-only peer"
                          checked={formData.openFlag === "Y"}
                          onChange={(e) => handleInputChange("openFlag", e.target.checked ? "Y" : "N")}
                        />
                        <div
                          className={`w-[3rem] h-[1.40625rem] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[1.59375rem] ${colors.controlThumb} after:content-[''] after:absolute after:top-[0.15625rem] after:left-[0.15625rem] after:rounded-full after:h-[1.09375rem] after:w-[1.09375rem] after:transition-all after:shadow-sm border ${formData.disclose === "Y" ? "border-none" : colors.borderPrimary} ${formData.openFlag === "Y" ? colors.bgActiveToggle : colors.bgInput}`}
                        ></div>
                        <span className={`ml-2 text-sm font-medium ${colors.textNormal}`}>
                          {formData.openFlag === "Y" ? t("common.yes") : t("common.no")}
                        </span>
                      </label>
                    </div>
                  </div> */}
                  {/* Department Privacy Toggle - replaces password field */}
                  {/* <div>
                    <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                      {t("qa.department-only")}
                    </label>
                    <div className="flex items-center pt-2 mb-2">
                      <label className="relative inline-flex cursor-pointer hover:cursor-pointer items-center justify-start">
                        <input
                          data-testid="qa-add-department-private-checkbox"
                          type="checkbox"
                          className="sr-only peer"
                          checked={formData.private}
                          onChange={(e) => handleInputChange("private", e.target.checked)}
                        />
                        <div
                          className={`w-[3rem] h-[1.40625rem] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[1.59375rem] ${colors.controlThumb} after:content-[''] after:absolute after:top-[0.15625rem] after:left-[0.15625rem] after:rounded-full after:h-[1.09375rem] after:w-[1.09375rem] after:transition-all after:shadow-sm border ${formData.private ? "border-none" : colors.borderPrimary} ${formData.private ? colors.bgActiveToggle : colors.bgInput}`}
                        ></div>
                        <span className={`ml-2 text-sm font-medium ${colors.textNormal}`}>
                          {formData.private ? t("common.yes") : t("common.no")}
                        </span>
                      </label>
                    </div>
                  </div> */}
                </div>

                {/* Question */}
                <div>
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("qa.question")} <span className={colors.textRequired}>*</span>
                  </label>
                  <CustomTextarea
                    data-testid="qa-add-question-textarea"
                    value={formData.question}
                    onChange={(e) => handleInputChange("question", e.target.value)}
                    placeholder={t("qa.question-placeholder")}
                    error={errors.question}
                    rows={6}
                    maxLength={CHARACTER_LENGTH.CONTENT}
                    className={`w-full rounded-lg border px-3 py-2 text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary} resize-none`}
                  />
                  <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid="qa-add-question-textarea-char-count">{(formData.question || "").length}/{CHARACTER_LENGTH.CONTENT}</p>
                </div>
              </div>
            </div>

            {/* Attachment Section */}
            <div className="mb-6" data-testid="qa-add-attachments-section">
              <h2
                className={`${colors.textPrimary} mb-2 text-lg font-semibold`}
                data-testid="qa-add-attachments-title"
              >
                {uppercase(t("bulletin.attachment"))}
              </h2>
              <FileUploader
                data-testid="qa-add-file-uploader"
                onFilesChange={(files) => setSelectedFiles(files)}
                selectedFiles={selectedFiles}
                multiple={true}
              />
            </div>
          </div>
        </div>
      </div>
      <FullScreenDotLoader
        loading={uploadFilesWithCustomCallback.isPending}
        text={
          uploadFilesWithCustomCallback.isPending
            ? t("bulletin.uploading-qna") || "Uploading Q&A file..."
            : t("bulletin.upload-qna-file") || "Uploading Q&A file..."
        }
        size={80}
        speedMultiplier={1.2}
      />
    </div>
  );
}
